import Tooltip from "@mui/material/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import React from "react";
import { useTranslation } from "react-i18next";
import ColorBlock from "./ColorBlock";
import { Color } from "../../../../core/models/entities/LogoOptions";
import {
  colorPickerLabel,
  isBackupWhiteRow,
} from "../../../../utils/systemColors";

function ColorSwitch({
  switchToColor,
  baseColor,
  isSelected,
  onClick,
  isBackup,
  isInDesignEditor,
}: {
  baseColor: Color;
  switchToColor?: Color;
  isSelected: boolean;
  onClick: (color: Color) => void;
  isBackup: boolean;
  isInDesignEditor?: boolean;
}) {
  const { t } = useTranslation();
  // The isBackup prop (colorDetails preferred, options fallback) alone is not
  // identity: catalog rows carry the same flag as "printed WITH white backup"
  // (regression 21-25/8 greyed out and relabelled ordinary colours). Only the
  // actual white/0821 layer row gets the backup treatment.
  const isBackupWhite =
    isBackup &&
    isBackupWhiteRow({
      id: baseColor.id,
      name: baseColor.name,
      nickname: baseColor.nickname,
      isBackup: true,
    });
  function LabelText({ color }: { color: Color }) {
    // Backup White keeps its dedicated label; normal white/black show the
    // translated system color name, everything else the raw nickname||name.
    const displayName = isBackupWhite
      ? t("logoEditorComponents.backupWhite")
      : colorPickerLabel(color, t);
    // The tooltip decision must follow the label the user actually sees — the
    // translated name can cross the ellipsis threshold even when the raw one
    // does not (e.g. "Schwarz" vs "Black").
    const hasTooltip = displayName.length > 5;
    const p = (
      <p className={"whitespace-nowrap overflow-hidden overflow-ellipsis"}>
        {displayName}
      </p>
    );

    return !hasTooltip ? (
      p
    ) : (
      <Tooltip placement={"top"} title={displayName}>
        {p}
      </Tooltip>
    );
  }

  // Tooltip content for the white-backup layer row only
  const backupTooltip = isBackupWhite
    ? t("logoEditorComponents.backupWhiteTooltip")
    : undefined;

  const content = (
    <div
      onClick={() => !isBackupWhite && onClick(baseColor)}
      className={`m-0.5 p-1 border rounded border-gray-200 h-[24px] flex items-center space-x-1.5 ${
        isBackupWhite
          ? "cursor-not-allowed bg-gray-100 text-gray-400"
          : "cursor-pointer hover:border-gray-600"
      } ${isSelected ? "border-gray-600" : ""} ${
        isInDesignEditor ? "w-[150px]" : "w-[220px]"
      }`}
    >
      <ColorBlock color={baseColor} />
      {!!switchToColor ? (
        <>
          <FontAwesomeIcon size={"sm"} icon={faArrowRight} />
          <ColorBlock color={switchToColor} />
          <LabelText color={switchToColor} />
        </>
      ) : (
        <LabelText color={baseColor} />
      )}
    </div>
  );

  return backupTooltip ? (
    <Tooltip
      placement="top"
      title={<span className="text-xs">{backupTooltip}</span>}
    >
      {content}
    </Tooltip>
  ) : (
    content
  );
}

export default ColorSwitch;
